import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {flashMessage, errorMessage} from 'shared/actions/notifications';
import Modal from 'shared/components/modal';
import Button from 'src/components/button';
import Loading from 'src/views/admin/Loading';
import AdminActionsService, {RepairOption} from 'src/services/AdminActionsService';

interface Props {
  userId: string;
  onClose: () => void;
  onRepaired: () => void;
}

type Step = 'inspect' | 'confirm';

const UserRepairModal = ({userId, onClose, onRepaired}: Props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [repairs, setRepairs] = useState<RepairOption[]>([]);
  const [valid, setValid] = useState(true);
  const [selectedRepair, setSelectedRepair] = useState<RepairOption | null>(null);
  const [step, setStep] = useState<Step>('inspect');
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    AdminActionsService.userRepairInspection(userId)
      .then((response) => {
        setRepairs(response.repairs);
        setValid(response.valid);
        setLoading(false);
      })
      .catch(() => {
        dispatch(errorMessage('Failed to load repair options'));
        onClose();
      });
  }, [userId]);

  const handleSelectRepair = (repair: RepairOption) => {
    setSelectedRepair(repair);
    setStep('confirm');
  };

  const handleRunRepair = async () => {
    if (!selectedRepair) return;
    setRepairing(true);
    try {
      await AdminActionsService.repairUser(userId, selectedRepair.key);
      dispatch(flashMessage('User repaired successfully!'));
      onRepaired();
    } catch {
      dispatch(errorMessage('Failed to repair user'));
      setRepairing(false);
    }
  };

  const handleBack = () => {
    setSelectedRepair(null);
    setStep('inspect');
  };

  return (
    <Modal show onClose={onClose} forceNotIonic>
      <Modal.TextHeader onClose={onClose}>Repair User</Modal.TextHeader>
      <Modal.Body>
        {loading && <Loading />}

        {!loading && step === 'inspect' && (
          <>
            {repairs.length === 0 ? (
              <div className={`alert ${valid ? 'alert-success' : 'alert-danger'}`} role="alert">
                {valid
                  ? 'No repairs needed — no issues detected.'
                  : 'This user is invalid but no automated repairs are available. Please reach out to engineering for assistance.'}
              </div>
            ) : (
              <>
                <p>The following issues were detected. Select one to repair:</p>
                {repairs.map((repair) => (
                  <div key={repair.key} className="panel panel-default">
                    <div className="panel-body">
                      <h4>{repair.label}</h4>
                      <p>{repair.description}</p>
                      {repair.details.length > 0 && (
                        <pre style={{fontSize: 11}}>{JSON.stringify(repair.details, null, 2)}</pre>
                      )}
                      <Button
                        onClick={() => handleSelectRepair(repair)}
                        buttonName={`select-repair-${repair.key}`}
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {!loading && step === 'confirm' && selectedRepair && (
          <>
            <div className="alert alert-warning" role="alert">
              <strong>Confirm repair: {selectedRepair.label}</strong>
            </div>
            <p>{selectedRepair.description}</p>
            {selectedRepair.details.length > 0 && (
              <>
                <p>
                  <strong>The following records will be affected:</strong>
                </p>
                <pre style={{fontSize: 11}}>{JSON.stringify(selectedRepair.details, null, 2)}</pre>
              </>
            )}
          </>
        )}
      </Modal.Body>

      {!loading && step === 'inspect' && repairs.length === 0 && (
        <Modal.Footer primaryButton={{text: 'Close', onClick: onClose}} />
      )}

      {!loading && step === 'confirm' && selectedRepair && (
        <Modal.Footer
          primaryButton={{
            text: repairing ? 'Repairing...' : 'Run Repair',
            disabled: repairing,
            onClick: handleRunRepair,
          }}
          secondaryButton={{
            text: 'Back',
            onClick: handleBack,
          }}
        />
      )}
    </Modal>
  );
};

export default UserRepairModal;
